/**
 * useSessionNotificationJoinability — current state of the sessions a batch of
 * notifications points at, so the bell and the notification centre can stop
 * offering "Join now" for rooms that never opened or already closed.
 *
 * One `in (...)` query per notification load, shared by both surfaces.
 */

import { useEffect, useState } from 'react';
import { loadSessionJoinability, type SessionJoinability } from '../lib/notificationService';
import { isJoinPromiseEvent, sessionIdFromNotification } from '../lib/sessionNotificationAffordance';

export interface JoinPromiseNotification {
  payload: {
    event?: string | null;
    data?: Record<string, unknown> | null;
    actionUrl?: string | null;
  };
}

export function useSessionNotificationJoinability(
  notifications: JoinPromiseNotification[],
): Record<string, SessionJoinability> {
  const [joinability, setJoinability] = useState<Record<string, SessionJoinability>>({});

  // A stable key so the effect fires on the set of ids, not on every re-render
  // of an equal array.
  const sessionIds = Array.from(
    new Set(
      notifications
        .filter((n) => isJoinPromiseEvent(n.payload?.event))
        .map((n) =>
          sessionIdFromNotification({
            data: n.payload?.data ?? null,
            actionUrl: n.payload?.actionUrl ?? null,
          }),
        )
        .filter((id): id is string => Boolean(id)),
    ),
  ).sort();
  const key = sessionIds.join(',');

  useEffect(() => {
    if (key.length === 0) {
      setJoinability({});
      return;
    }
    let cancelled = false;
    void loadSessionJoinability(key.split(','))
      .then((states) => {
        if (!cancelled) setJoinability(states);
      })
      .catch(() => {
        /* leave it unresolved — an unknown target keeps the sender's action */
      });
    return () => {
      cancelled = true;
    };
  }, [key]);

  return joinability;
}
